import { validateUpload } from './uploadValidation';
// Browser safeguards supplement, but cannot replace, API enforcement.
export const safeMessage = (value, fallback = '') => {
  if (typeof value !== 'string') return fallback;
  const text = value.trim();
  if (!text || text.length > 300 || /[<>]|\[object Object\]|SQLSTATE|SELECT\s|INSERT\s|stack trace|SyntaxError|Unexpected token|JSON\.parse|JSON at position|JSON input|node_modules|\/var\/|\/home\/|ENOENT|ECONN|REACT_APP_/i.test(text)) return fallback;
  return text;
};

export const safeErrorMessage = (error) => safeMessage(error?.message,
  'Unable to complete the request. Please try again or contact support.');

export const readJson = async (response) => {
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('Unable to read the server response. Please try again or contact support.');
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if ('message' in data) data.message = safeMessage(data.message, 'Unable to complete the request. Please try again.');
    if (typeof data.error === 'string') data.error = safeMessage(data.error, 'Unable to complete the request.');
  }
  return data;
};

const credentialField = /^(password|password_hash|passwordHash|password_confirmation|currentPassword|oldPassword|newPassword|confirmPassword|old|new|token|authToken|access_token|refresh_token)$/i;
// Preserve passwords verbatim: they are not ordinary business text fields.
export const validateBusinessPayload = (value, field = '', depth = 0) => {
  if (credentialField.test(field)) return;
  if (depth > 20) throw new Error('The form contains too many nested values.');
  if (['__proto__', 'prototype', 'constructor'].includes(field)) throw new Error('The form contains an unsupported field.');
  if (Array.isArray(value)) {
    value.forEach(item => validateBusinessPayload(item, field, depth + 1));
  } else if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, item]) => validateBusinessPayload(item, key, depth + 1));
  } else if (typeof value === 'string') {
    if (value.length > 10000) throw new Error('Keep each text field within 10,000 characters.');
    if ([...value].some(char => { const code = char.charCodeAt(0); return (code < 32 && ![9, 10, 13].includes(code)) || code === 127; })) throw new Error('Remove unsupported control characters from the form.');
    if (/^(qty|quantity|rate|gst|discount|pack_size|no_of_packs)$/i.test(field) && /^(NaN|[-+]?Infinity)$/i.test(value.trim())) throw new Error('Please enter a valid number.');
    if (/<\/?[a-z!][^>]*>|javascript\s*:/i.test(value) || value.includes(String.fromCharCode(0))) {
      throw new Error('HTML, script content and null characters are not allowed in business fields.');
    }
  } else if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new Error('Please enter a valid number.');
  }
};

export const validatedFetch = async (input, options = {}) => {
  if (/^(POST|PUT|PATCH)$/i.test(options.method || '') && typeof options.body === 'string') {
    let payload;
    try { payload = JSON.parse(options.body); } catch { throw new Error('Unable to prepare the request. Please try again.'); }
    validateBusinessPayload(payload);
  }
  return fetch(input, options);
};

export const validateLogo = file => validateUpload(file, { imagesOnly: true });

export const validNumber = (value, label, { min = 0, max = Number.MAX_SAFE_INTEGER } = {}) => {
  if (!['string', 'number'].includes(typeof value) || String(value).trim() === '') throw new Error(`${label} is required.`);
  const number = Number(value);
  if (!Number.isFinite(number) || number < min || number > max) throw new Error(`${label} must be a number between ${min} and ${max}.`);
  return number;
};

const authKeys = ['rcsSessionVersion', 'authToken', 'accessToken', 'token', 'user', 'loginResponse', 'rangeData', 'userRole', 'isAuthenticated', 'showVegPriceAlert'];
export const clearClientAuth = () => {
  authKeys.forEach(key => { localStorage.removeItem(key); sessionStorage.removeItem(key); });
  window.dispatchEvent(new Event('rcs-session-change'));
};

export const withoutPasswordFields = (value) => {
  if (Array.isArray(value)) return value.map(withoutPasswordFields);
  if (typeof value === 'string' && ['[', '{'].includes(value.trim().charAt(0))) {
    try { return JSON.stringify(withoutPasswordFields(JSON.parse(value))); } catch { return value; }
  }
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !/password|passwd|pwd|secret|^(token|authToken|accessToken|access_token|refreshToken|refresh_token|rcs_session|sessionId|session_id)$/i.test(key))
    .map(([key, item]) => [key, withoutPasswordFields(item)]));
};
